import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';

import type {
	DataSecurityPolicyAction,
	useIsAttachmentDownloadDisabledQuery$data,
	useIsAttachmentDownloadDisabledQuery$variables,
	DataSecurityPolicyDecidableContentStatus,
} from './__generated__/useIsAttachmentDownloadDisabledQuery.graphql';

interface UseIsAttachmentDownloadDisabledArgs {
	contentId: string;
	spaceKey?: string | null;
	skip?: boolean;
}

const useIsAttachmentDownloadDisabledQuery = graphql`
	query useIsAttachmentDownloadDisabledQuery(
		$action: DataSecurityPolicyAction!
		$contentId: ID!
		$contentStatus: DataSecurityPolicyDecidableContentStatus!
		$contentVersion: Int!
		$spaceKey: String
	) {
		dataSecurityPolicy {
			isActionEnabledForContent(
				action: $action
				contentId: $contentId
				contentStatus: $contentStatus
				contentVersion: $contentVersion
				spaceKey: $spaceKey
			) {
				action
				allowed
			}
		}
	}
`;

export const getAttachmentDSPQueryVariables = (contentId: string, spaceKey?: string | null) => ({
	action: 'PAGE_EXPORT' as DataSecurityPolicyAction,
	contentId,
	contentStatus: 'CURRENT' as DataSecurityPolicyDecidableContentStatus,
	contentVersion: 1,
	spaceKey,
});

export const useIsAttachmentDownloadDisabled = ({
	contentId,
	spaceKey,
	skip,
}: UseIsAttachmentDownloadDisabledArgs) => {
	const { data, loading, error } = useQueryInterop<
		useIsAttachmentDownloadDisabledQuery$data,
		useIsAttachmentDownloadDisabledQuery$variables
	>(useIsAttachmentDownloadDisabledQuery, {
		variables: getAttachmentDSPQueryVariables(contentId, spaceKey),
		context: {
			allowOnExternalPage: true,
		},
		skip,
	});
	const allowedResp = data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
	// Defaults to disabled if we don't have a definitive answer
	const isDisabled = allowedResp === undefined || allowedResp === null ? true : !allowedResp;
	return {
		isDisabled,
		loading,
		error,
	};
};
